import { FC, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@chakra-ui/react';
import { PaperPlaneTilt, SoccerBall } from '@phosphor-icons/react';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { AssignFieldWorkoutBody } from '../../../models/field-programming';
import AssignStep from '../FieldWorkoutBuilderModal/steps/AssignStep';
import styles from './FieldAssignModal.module.scss';

interface Props {
  workoutId: string;
  workoutName: string;
  onClose: () => void;
}

const FieldAssignModal: FC<Props> = ({ workoutId, workoutName, onClose }) => {
  const api = useEndpoints();
  const qc = useQueryClient();
  const [data, setData] = useState<AssignFieldWorkoutBody>({
    scheduledDates: [],
  });

  const mut = useMutation({
    mutationFn: () =>
      api.fieldProgramming.workouts.assign(workoutId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coachSessions'] });
      qc.invalidateQueries({ queryKey: ['fieldProgramming', 'workouts'] });
      onClose();
    },
  });

  const canSubmit = !!(
    (data.teamId || (data.athleteIds && data.athleteIds.length > 0)) &&
    data.scheduledDates.length > 0
  );

  const Header = (
    <div className={styles.header}>
      <div className={styles.headerColorBar} />
      <div className={styles.headerContent}>
        <div className={styles.headerTitleRow}>
          <div className={styles.headerIcon}>
            <SoccerBall size={20} weight="bold" />
          </div>
          <div>
            <p className={styles.headerTitle}>Assegna scheda Campo</p>
            <p className={styles.headerSubtitle}>
              <span className={styles.workoutNameHighlight}>{workoutName}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const Footer = (
    <div className={styles.footer}>
      <button
        type="button"
        className={styles.cancelBtn}
        onClick={onClose}
        disabled={mut.isPending}
      >
        Annulla
      </button>
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() => mut.mutate()}
        disabled={!canSubmit || mut.isPending}
      >
        {mut.isPending ? <Spinner size="xs" /> : <PaperPlaneTilt size={14} weight="bold" />}
        {mut.isPending ? 'Assegnazione...' : 'Assegna'}
      </button>
    </div>
  );

  return (
    <GenericModalV2
      isOpen={true}
      onClose={onClose}
      size="xl"
      Header={Header}
      Footer={Footer}
      isLoading={mut.isPending}
    >
      <div className={styles.body}>
        <AssignStep data={data} onChange={(d) => setData((p) => ({ ...p, ...d }))} />
      </div>
    </GenericModalV2>
  );
};

export default FieldAssignModal;
